import { CMSData, CtaComponentProps } from "@/cms/models";
import { TypographyComponentProps } from "@/components/ui/TypographyComponent";

export function mergeCtaSettings(
  config: any,
  cmsData: CMSData,
  defaultValues?: Partial<CtaComponentProps>
): CtaComponentProps {
  return {
    ...{
      textColor: defaultValues?.textColor ?? cmsData.theme.button.textColor,
      bgColor: defaultValues?.bgColor ?? cmsData.theme.button.bgColor,
      borderColor: defaultValues?.borderColor ?? "inherit",
      hoverBgColor:
        defaultValues?.hoverBgColor ?? cmsData.theme.button.hoverBgColor,
      hoverTextColor:
        config.hoverTextColor ?? cmsData.theme.button.hoverTextColor,
      size: defaultValues?.size ?? "sm",
    },
    ...config,
  };
}

export function mergeTypographySettings(
  config: any,
  cmsData: CMSData,
  defaultValues?: Partial<TypographyComponentProps>
): TypographyComponentProps {
  return {
    ...{
      textColor: cmsData.theme.page.textColor,
      size: defaultValues?.size ?? "sm",
      padding: defaultValues?.padding ?? "none",
    },
    ...config,
  };
}

function buildPadding(padding: string, onlyDesktop: boolean) {
  const paddings = padding.split(" ");
  const desktopPrefix = onlyDesktop ? `md:` : "";
  switch (paddings.length) {
    case 0:
      return "";
    case 1:
      return `${desktopPrefix}homepage-p-${paddings[0]}`;
    case 2:
      return `${desktopPrefix}homepage-pt-${paddings[0]} ${desktopPrefix}homepage-pb-${paddings[0]} ${desktopPrefix}homepage-pl-${paddings[1]} ${desktopPrefix}homepage-pr-${paddings[1]}`;
    case 3:
      return `${desktopPrefix}homepage-pt-${paddings[0]} ${desktopPrefix}homepage-pb-${paddings[0]} ${desktopPrefix}homepage-pl-${paddings[1]} ${desktopPrefix}homepage-pr-${paddings[1]}`;
    case 4:
      return `${desktopPrefix}homepage-pt-${paddings[0]} ${desktopPrefix}homepage-pr-${paddings[1]} ${desktopPrefix}homepage-pb-${paddings[2]} ${desktopPrefix}homepage-pl-${paddings[3]}`;
  }
}

function buildMargin(margin: string, onlyDesktop: boolean) {
  const margins = margin.split(" ");
  const desktopPrefix = onlyDesktop ? `md:` : "";
  switch (margins.length) {
    case 0:
      return "";
    case 1:
      return `${desktopPrefix}homepage-m-${margins[0]}`;
    case 2:
      return `${desktopPrefix}homepage-mt-${margins[0]} ${desktopPrefix}homepage-mb-${margins[0]} ${desktopPrefix}homepage-ml-${margins[1]} ${desktopPrefix}homepage-mr-${margins[1]}`;
    case 3:
      return `${desktopPrefix}homepage-mt-${margins[0]} ${desktopPrefix}homepage-mb-${margins[0]} ${desktopPrefix}homepage-ml-${margins[1]} ${desktopPrefix}homepage-mr-${margins[1]}`;
    case 4:
      return `${desktopPrefix}homepage-mt-${margins[0]} ${desktopPrefix}homepage-mr-${margins[1]} ${desktopPrefix}homepage-mb-${margins[2]} ${desktopPrefix}homepage-ml-${margins[3]}`;
  }
}

export function getMargins(margin?: string) {
  if (!margin) {
    return "";
  }
  const responsiveMargins = margin.split("|");
  if (responsiveMargins.length === 1) {
    return buildMargin(responsiveMargins[0].trim(), false);
  }
  return (
    buildMargin(responsiveMargins[0].trim(), false) +
    " " +
    buildMargin(responsiveMargins[1].trim(), true)
  );
}

export function getTextSize(size?: string, defaultValue: string = "sm") {
  return `homepage-text-${getMobileSize(size, defaultValue)} md:homepage-text-${getDesktopSize(size, defaultValue)}`;
}

export function getMobileFlowDirection(direction?: string) {
  if (!direction) {
    return "vertical";
  }
  const responsiveDirection = direction.split("|");
  return responsiveDirection[0].trim();
}

export function getDesktopFlowDirection(direction?: string) {
  if (!direction) {
    return "vertical";
  }
  const responsiveDirection = direction.split("|");
  return responsiveDirection[1]?.trim() ?? responsiveDirection[0].trim();
}

export function getMobileSize(size?: string, defaultValue: string = "sm") {
  if (!size) {
    return defaultValue;
  }
  const responsiveSize = size.split("|");
  if (responsiveSize.length === 0) {
    return defaultValue;
  }
  return responsiveSize[0].trim();
}

export function getDesktopSize(size?: string, defaultValue: string = "sm") {
  if (!size) {
    return defaultValue;
  }
  const responsiveSize = size.split("|");
  if (responsiveSize.length === 1) {
    return responsiveSize[0].trim();
  }
  return responsiveSize[1].trim();
}

export function getMobileHeight(height?: string) {
  if (!height) {
    return "auto";
  }

  const responsiveHeight = height.split("|");
  if (responsiveHeight.length === 0) {
    return "auto";
  }

  return responsiveHeight[0].trim();
}

export function getDesktopHeight(height?: string) {
  if (!height) {
    return "auto";
  }
  const responsiveHeight = height.split("|");
  if (responsiveHeight.length === 1) {
    return responsiveHeight[0].trim();
  }
  return responsiveHeight[1].trim();
}

export function getPaddings(padding?: string) {
  if (!padding) {
    return "";
  }

  const responsivePaddings = padding.split("|");
  if (responsivePaddings.length === 1) {
    return buildPadding(responsivePaddings[0].trim(), false);
  }
  return (
    buildPadding(responsivePaddings[0].trim(), false) +
    " " +
    buildPadding(responsivePaddings[1].trim(), true)
  );
}

export function getRounded(rounded?: string) {
  if (!rounded) {
    return "rounded-none";
  }

  const mobile = getMobile("none", rounded);
  const desktop = getDesktop("none", rounded);

  return `${buildCorners(mobile, "", "rounded")} ${buildCorners(desktop, "md:", "rounded")}`;
}

export function getShadow(shadow?: string) {
  const mobile = getMobile("none", shadow);
  const desktop = getDesktop("none", shadow);

  return `${getShadowSingle(mobile)} hover:${getShadowSingle(desktop)} ease-in-out duration-300`;
}

export function getShadowSingle(shadow?: string) {
  switch (shadow) {
    case "none":
      return "";

    case "sm":
      return "shadow-sm";
    case "md":
      return "shadow-md";
    case "lg":
      return "shadow-lg";
    case "xl":
      return "shadow-xl";
    case "2xl":
      return "shadow-2xl";

    default:
      return "";
  }
}

export function buildCorners(
  values: string,
  prefix: string,
  valueName: string
) {
  const margins = values.split(" ");

  switch (margins.length) {
    case 0:
      return "";
    case 1:
      return `${prefix}${valueName}-${margins[0]}`;
    case 2:
      return `${prefix}${valueName}-tl-${margins[0]} ${prefix}${valueName}-tr-${margins[1]} ${prefix}${valueName}-br-${margins[0]} ${prefix}${valueName}-bl-${margins[1]}`;
    case 3:
      return `${prefix}${valueName}-tl-${margins[0]} ${prefix}${valueName}-tr-${margins[1]} ${prefix}${valueName}-br-${margins[2]} ${prefix}${valueName}-bl-${margins[1]}`;
    case 4:
      return `${prefix}${valueName}-tl-${margins[0]} ${prefix}${valueName}-tr-${margins[1]} ${prefix}${valueName}-br-${margins[2]} ${prefix}${valueName}-bl-${margins[3]}`;
  }
}

export function getVisibleValue(boolValue?: string) {
  if (!boolValue) {
    return "";
  }

  const responsiveBooleanValues = boolValue.split("|");

  if (responsiveBooleanValues.length === 1) {
    return responsiveBooleanValues[0].trim() === "true"
      ? ""
      : "!hidden !md:hidden";
  }

  let visibilityClass =
    responsiveBooleanValues[0].trim() === "false" ? "hidden" : "flex";
  visibilityClass +=
    responsiveBooleanValues[1].trim() === "false" ? " md:hidden" : " md:flex";

  console.log(visibilityClass);
  return visibilityClass;
}

export function getGap(gap?: string, defaultValue: string = "") {
  if (!gap) {
    if (!defaultValue) {
      return "";
    }

    return `homepage-gap-${defaultValue}`;
  }

  const responsiveGaps = gap.split("|");
  if (responsiveGaps.length === 1) {
    return `homepage-gap-${responsiveGaps[0].trim()}`;
  }
  return `homepage-gap-${responsiveGaps[0].trim()} md:homepage-gap-${responsiveGaps[1].trim()}`;
}

export function getTestimonialImageSize(size?: string) {
  switch (size) {
    case "sm":
      return "120px";
    case "md":
      return "160px";
    case "lg":
      return "200px";
    case "xl":
      return "240px";
    default:
      return "120px";
  }
}

export function getMobile(defaultValue: string, input?: string) {
  if (!input) {
    return defaultValue;
  }
  const responsiveHeight = input.split("|");

  return responsiveHeight[0].trim();
}

export function getDesktop(defaultValue: string, input?: string) {
  if (!input) {
    return defaultValue;
  }
  const responsiveHeight = input.split("|");
  if (responsiveHeight.length === 1) {
    return responsiveHeight[0].trim();
  }
  return responsiveHeight[1].trim();
}

export function getBorderRadius(rounded?: string, type?: string) {
  if (!rounded) {
    return "inherit";
  }

  if (
    type === "image" ||
    type === "card" ||
    type === "video" ||
    type === "section"
  ) {
    rounded = rounded
      .split(" ")
      .map((x) => {
        switch (x) {
          case "none":
            return "none";
          case "little":
            return "md";
          case "lots":
            return "xl";
          case "max":
            return "2xl";
          default:
            return x;
        }
      })
      .join(" ");
  } else if (type === "button") {
    rounded = rounded
      .split(" ")
      .map((x) => {
        switch (x) {
          case "none":
            return "none";
          case "little":
            return "md";
          case "lots":
            return "xl";
          case "max":
            return "full";
          default:
            return x;
        }
      })
      .join(" ");
  } else {
    rounded = rounded
      .split(" ")
      .map((x) => {
        switch (x) {
          case "none":
            return "none";
          case "little":
            return "md";
          case "lots":
            return "xl";
          case "max":
            return "full";
          default:
            return x;
        }
      })
      .join(" ");
  }

  return rounded
    .split(" ")
    .map((x) => getBorderRadiusSingle(x))
    .join(" ");
}

export function getBorderRadiusSingle(rounded?: string) {
  switch (rounded) {
    case "none":
      return "0px";
    case "theme":
      return "inherit";
    case "sm":
      return "0.125rem";
    case "md":
      return "0.375rem";
    case "lg":
      return "0.5rem";
    case "xl":
      return "0.75rem";
    case "2xl":
      return "1rem";
    case "3xl":
      return "1.5rem";
    case "full":
      return "9999px";
    default:
      return "inherit";
  }
}
